// Enhanced Dashboard.js - Cleaned with extracted hooks and comprehensive cleanup
import React, { useEffect, useState } from "react";
import "./css-styles/index.css";
import { useNavigate } from "react-router-dom";
import { PlayerProvider, usePlayerContext } from '../hooks/PlayerContext';
import { useSyncOperation, SyncOperations } from '../hooks/SyncCoordinator';
import usePlatformData from '../hooks/usePlatformData';
import logger from '../services/logger';
import SpotifyDashboardSearch from './refracted Dashboard/SpotifyDashboardSearch';
// PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
// import YouTubeDashboardSearch from './refracted Dashboard/YouTubeDashboardSearch';

// Component imports
import UserProfile from './refracted Dashboard/UserProfile';
import ParallaxBackground from './refracted Dashboard/ParallaxBackground';
import MediaCards from './refracted Dashboard/MediaCards';
import SpotifyMusicPlayer from './Spotify/SpotifyMusicPlayer';
import SpotifyPlayer from './Spotify/SpotifyPlayer';
// PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
// import YouTubeMusicPlayer from './Youtube/YouTubeMusicPlayer';

// Import extracted hooks
import { useSessionCleanup } from '../hooks/useSessionCleanup';
import { usePlatformManagement } from '../hooks/usePlatformManagement';
import { useGuestTimer } from '../hooks/useGuestTimer';
import { useEnhancedUserBubble } from '../hooks/useEnhancedUserBubble';

// Import pure component
import { EnhancedUserBubble } from './EnhancedUserBubble';

// Dashboard Content
const DashboardContent = () => {
  const navigate = useNavigate();
  const {
    state,
    setUIState
  } = usePlayerContext();
  const { runSync } = useSyncOperation();
  const { fetchData } = usePlatformData();

  const [tokenRefreshFailed, setTokenRefreshFailed] = useState(false);

  // Use extracted hooks for all logic
  const { 
    handleDashboardSessionExpired,
    createEnhancedMongoLogout,
    handleBackToPlatformsWithCleanup
  } = useSessionCleanup();
  
  const {
    platformStates,
    handleDashboardPlatformLogout,
    checkPlatformStates,
    handleSpotifyAction,
    // PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
    // handleYouTubeAction
  } = usePlatformManagement();
  
  const {
    isGuest,
    guestTimeRemaining,
    formatTime
  } = useGuestTimer(navigate, handleDashboardSessionExpired);
  
  const {
    user
  } = useEnhancedUserBubble(navigate, 'dashboard');

  // Create the enhanced MongoDB logout function
  const handleDashboardMongoLogout = createEnhancedMongoLogout(user, platformStates, navigate);

  // Enhanced Back to Platforms Handler with Comprehensive Cleanup
  const handleBackToPlatforms = async () => {
    await handleBackToPlatformsWithCleanup(navigate, setUIState);
  };

  // Initialize user data and platform states
  useEffect(() => {
    const initializeDashboardBubble = async () => {
      // Check platform states
      await checkPlatformStates();
    };

    initializeDashboardBubble();
  }, [checkPlatformStates]); // Include checkPlatformStates dependency

  // Enhanced platform theming with detection
  useEffect(() => {
    // Apply platform-specific body class for theming
    const applyPlatformTheme = (platform) => {
      // Remove any existing platform classes
      document.body.classList.remove('platform-spotify', 'platform-youtube');

      // Add current platform class
      if (platform === 'spotify') {
        document.body.classList.add('platform-spotify');
        logger.platform('Applied Spotify theme');
      }
      // PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
      // else if (platform === 'youtube') {
      //   document.body.classList.add('platform-youtube');
      //   logger.platform('Applied YouTube theme');
      // }
    };

    // Apply theme when platform changes
    if (state.platform) {
      applyPlatformTheme(state.platform);
    }

    // Apply theme to sidebar specifically
    const sidebar = document.querySelector('.music-sidebar');
    if (sidebar && state.platform) {
      sidebar.classList.remove('platform-spotify', 'platform-youtube');
      sidebar.classList.add(`platform-${state.platform}`);
    }

    // Cleanup function to remove platform classes when component unmounts
    return () => {
      document.body.classList.remove('platform-spotify', 'platform-youtube');
      const sidebarElement = document.querySelector('.music-sidebar');
      if (sidebarElement) {
        sidebarElement.classList.remove('platform-spotify', 'platform-youtube');
      }
    };
  }, [state.platform]);

  // Removed complex platform detection - let fetchData handle it

  // Removed platform switching detection - handled by theming effect
  
  // Simple data fetch on mount (like working backup)
  useEffect(() => {
    runSync(SyncOperations.AUTH, async () => {
      await fetchData();
    });
  }, [fetchData, runSync]);
  
  // Handle logout with platform cleanup
  // eslint-disable-next-line no-unused-vars
  const handleLogout = async () => {
    try {
      await runSync(SyncOperations.AUTH, async () => {
        // Clear any playing audio
        if (window._spotifyPlayerInstance) {
          try {
            await window._spotifyPlayerInstance.pause();
            window._spotifyPlayerInstance.disconnect();
          } catch (e) {
            logger.warn('Error cleaning up player:', e);
          }
        }
        
        // Clean up platform theming
        document.body.classList.remove('platform-spotify', 'platform-youtube');
        document.title = 'Phantom';
        
        // Clear session
        await fetch("/logout", {
          method: "GET",
          credentials: "include",
        });
        
        // Navigate away
        navigate("/");
      });
    } catch (err) {
      logger.error("Logout failed", err);
      // Force navigation anyway
      navigate("/");
    }
  };
  
  // Error recovery with platform awareness
  const handleReconnect = async () => {
    setTokenRefreshFailed(false);
    await runSync(SyncOperations.AUTH, async () => {
      await fetchData();
    });
  };
  
  // Render loading state
  if (state.loading) {
    return (
      <>
        <ParallaxBackground />
        <div className="hero">
          <div className="hero-content fade-in">
            <div className="loading-spinner">
              <div className="loader-spinner"></div>
              <p>Loading your music...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ParallaxBackground />
      
      <div className="hero">
        <div className="hero-content fade-in">
          {/* Updated dashboard header section */}
          <div className="dashboard-header-section">
            <h1>
              🎵 Phantom Dashboard
            </h1>
            <p>Your AI-enhanced music dashboard</p>
            
            {/* Enhanced user bubble with unified cleanup handler */}
            {(user || isGuest) && (
              <EnhancedUserBubble
                navigate={navigate}
                formatTime={formatTime}
                guestTimeRemaining={guestTimeRemaining}
                platformStates={platformStates}
                onPlatformLogout={(platform) => handleDashboardPlatformLogout(platform, state.platform, navigate)}
                onMongoLogout={handleDashboardMongoLogout}
                onBackToPlatforms={handleBackToPlatforms}
                showBackToPlatforms={true}
                currentPage="dashboard"
                handleSpotifyAction={handleSpotifyAction}
                // PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music)
                // handleYouTubeAction={handleYouTubeAction}
              />
            )}
            
            {/* Connection error handling */}
            {(state.connectionStatus === 'error' || tokenRefreshFailed) && (
              <div className="connection-error">
                <p>Connection to {state.platform} lost. Please reconnect.</p>
                <button onClick={handleReconnect} className="reconnect-button">
                  Reconnect
                </button>
              </div>
            )}
            
            <div className="dashboard-section">
              <UserProfile />
              {state.platform === 'spotify' && <SpotifyDashboardSearch />}
              {/* PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music) */}
              {/* {state.platform === 'youtube' && <YouTubeDashboardSearch />} */}
              <MediaCards />
            </div>
          </div>
        </div>
      </div>
      
      {/* Music Player Components - Only show if we have tracks */}
      {state.playlist.tracks.length > 0 && state.platform === 'spotify' && (
        <>
          {/* Radio Mode Toggle */}
          <SpotifyMusicPlayer disableOldRadio={false} />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            pointerEvents: 'none'
          }}>
            <SpotifyPlayer />
          </div>
        </>
      )}

      {/* PHASE 1: YouTube disabled for Spotify-only launch (Phase 2: Apple Music) */}
      {/* YouTube player */}
      {/* {state.playlist.tracks.length > 0 && state.platform === 'youtube' && (
        <YouTubeMusicPlayer />
      )} */}
    </>
  );
};

// Main Dashboard component with all providers
const Dashboard = () => {
  return (
    <PlayerProvider>
      <DashboardContent />
    </PlayerProvider>
  );
};

export default Dashboard;